// /opt/roadoptix/frontend/src/pages/ShipperLoadDetail.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { apiGetShipper, getApiBase } from "../api";
import { useAuth } from "../components/AuthContext";

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return String(dt);
  }
}

function money(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(Number(cents) / 100).toFixed(2)}`;
}

function section(title, children) {
  return (
    <div style={{ marginTop: 16, padding: 16, border: "1px solid #e5e5e5", borderRadius: 12, background: "white" }}>
      <div style={{ fontWeight: 900, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

function statusTone(type) {
  const t = String(type || "").toUpperCase();
  if (["DELIVERED", "CLOSED"].includes(t)) return { background: "#eaffea", borderColor: "#9be79b" };
  if (["CANCELLED", "REJECTED"].includes(t)) return { background: "#ffecec", borderColor: "#ffb3b3" };
  if (["PICKED_UP", "ACCEPTED", "TENDERED", "POSTED"].includes(t)) return { background: "#fff7db", borderColor: "#ffd56a" };
  return { background: "#f6f6f6", borderColor: "#ddd" };
}

function statusLabel(type) {
  const t = String(type || "").toUpperCase();
  if (t === "STATUS_CHANGED") return "Status Changed";
  if (t === "DOCUMENT_UPLOADED") return "Document Uploaded";
  if (t === "DOCUMENT_REVIEWED") return "Document Reviewed";
  if (t === "COMPLIANCE_CHECKED") return "Compliance Checked";
  return t.replaceAll("_", " ");
}

export default function ShipperLoadDetail() {
  const nav = useNavigate();
  const loc = useLocation();
  const { id } = useParams();
  const apiBase = useMemo(() => getApiBase(), []);

  const { shipperEmail: authShipperEmail } = useAuth();
  const [shipperEmail, setShipperEmail] = useState(loc.state?.shipperEmail || authShipperEmail || "");
  const [shipperCompanyId, setShipperCompanyId] = useState(loc.state?.shipperCompanyId || "");

  const [load, setLoad] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function refresh() {
    setErr("");
    setLoading(true);
    try {
      const data = await apiGetShipper(`/shipper/loads/${id}`, { shipperEmail, shipperCompanyId: shipperCompanyId || undefined });
      setLoad(data.load || null);
    } catch (e) {
      setErr(e.message || String(e));
      setLoad(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timeline = useMemo(() => {
    const events = (load?.events || []).slice().reverse(); // oldest -> newest
    return events;
  }, [load]);

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <PageHeader title="Shipper Load Detail" subtitle={load ? `${load.referenceNumber || load.id}` : id} />

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button onClick={() => nav("/app/shipper/loads", { state: { shipperEmail, shipperCompanyId } })} style={{ padding: "8px 12px" }}>
          ← Back to Loads
        </button>

        <div style={{ marginLeft: 6 }}>
          <div style={{ fontSize: 13, color: "#666" }}>Shipper Email</div>
          <input value={shipperEmail} onChange={(e) => setShipperEmail(e.target.value)} style={{ width: 320, padding: 8 }} />
        </div>

        <div>
          <div style={{ fontSize: 13, color: "#666" }}>Shipper Company ID (optional)</div>
          <input
            value={shipperCompanyId}
            onChange={(e) => setShipperCompanyId(e.target.value)}
            placeholder="Only needed if user has multiple shipper memberships"
            style={{ width: 360, padding: 8 }}
          />
        </div>

        <button onClick={refresh} style={{ padding: "10px 14px", fontWeight: 900 }} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>

        <div style={{ marginLeft: "auto", fontSize: 12, color: "#666" }}>API: {apiBase}</div>
      </div>

      {err ? (
        <div style={{ marginTop: 14, padding: 12, background: "#ffecec", border: "1px solid #ffb3b3", borderRadius: 10 }}>
          {err}
        </div>
      ) : null}

      {section(
        "Overview",
        loading ? (
          <div>Loading…</div>
        ) : !load ? (
          <div style={{ color: "#666" }}>No load found.</div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 900, fontSize: 18 }}>{load.referenceNumber || load.id}</div>
              <div style={{ color: "#666" }}>
                Status: <b>{load.status}</b> • Posted: <b>{String(load.isPosted)}</b>
              </div>
              <div style={{ marginLeft: "auto", color: "#666" }}>
                Pickup <b>{fmt(load.pickupStart)}</b>
              </div>
            </div>

            <div>
              <b>From:</b> {load.originAddress || "—"} <span style={{ color: "#aaa" }}>→</span>{" "}
              <b>To:</b> {load.destAddress || "—"}
            </div>

            <div style={{ color: "#555" }}>
              <b>Carrier:</b> {load.carrier?.name || "—"} &nbsp;&nbsp; <b>Equipment:</b> {load.equipmentType || "—"}{" "}
              &nbsp;&nbsp; <b>Rate:</b> {money(load.rateAmountCents)} &nbsp;&nbsp;{" "}
              <b>Distance:</b> {load.distanceMiles ?? "—"} mi
            </div>
          </div>
        )
      )}

      {section(
        "Status Timeline",
        loading ? (
          <div>Loading…</div>
        ) : !timeline.length ? (
          <div style={{ color: "#666" }}>No events yet.</div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {timeline.map((e) => {
              const tone = statusTone(e.type);
              return (
                <div
                  key={e.id}
                  style={{
                    border: `1px solid ${tone.borderColor}`,
                    background: tone.background,
                    borderRadius: 12,
                    padding: 12,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 900 }}>{statusLabel(e.type)}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>{fmt(e.createdAt)}</div>
                    <div style={{ marginLeft: "auto", fontSize: 12, color: "#666" }}>
                      {e.actorUser?.email ? <>by {e.actorUser.email}</> : null}
                    </div>
                  </div>
                  <div style={{ marginTop: 6, color: "#333" }}>{e.message}</div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
